import { FeedbackSentiment, SOURCE_TYPE } from '@/types/feedbackConst';

enum BlockType {
  DEFAULT,
  FOCUSED,
}

const getTagByBlockType = (type: BlockType, block: FeedbackBlockT) => {
  switch (type) {
    case BlockType.FOCUSED:
      return {
        startTag: `<span class="content-body-tag__default sentiment_${
          FeedbackSentiment[block.sentiment]
        } block-focused" data-block-id=${
          block.alias
        }><span class="tooltip-anchor"></span>`,
        endTag: '</span>',
      };
    default:
      return {
        startTag: `<span class="content-body-tag__default sentiment_${
          FeedbackSentiment[block.sentiment]
        }" data-block-id=${block.alias}><span class="tooltip-anchor"></span>`,
        endTag: '</span>',
      };
  }
};

const setBlockTags = (params: {
  text: string;
  blocks: FeedbackBlockT[];
  source_id?: string;
  focusedBlock?: string;
}): string => {
  const flattenBlocks = params.blocks
    .reduce((acc, block) => {
      for (const location of block.locations || []) {
        acc.push({
          ...block,
          locations: [location],
        });
      }
      return acc;
    }, [] as FeedbackBlockT[])
    .sort((a, b) => a.locations[0].begin_offset - b.locations[0].begin_offset);

  const isOtherSource = (source_id?: string) =>
    typeof params.source_id === 'string' && source_id !== params.source_id;

  const mappedBlocks = flattenBlocks
    .reduce(
      (acc, block) => {
        const targetLocation = block.locations.find(
          (loc) => !isOtherSource(loc.message_source_id),
        );
        if (targetLocation) {
          acc.push({
            ...block,
            location: targetLocation,
          });
        }
        return acc;
      },
      [] as (FeedbackBlockT & {
        location: FeedbackBlockLocationT;
      })[],
    )
    .sort((a, b) => a.location.begin_offset - b.location.begin_offset);
  let result = params.text;
  let globalOffset = 0;
  for (const block of mappedBlocks) {
    if (!params.source_id) {
      // skip block if it has message_source_id and source_id is not provided
      // In discussion-type feedback, a single block may extend across both the main body and the comment section.
      if (block.location.message_source_id) continue;
    }
    const { begin_offset: originStart, end_offset: originEnd } = block.location;
    const start = originStart + globalOffset;
    const end = originEnd + globalOffset;
    const tag = getTagByBlockType(
      block.alias === params.focusedBlock
        ? BlockType.FOCUSED
        : BlockType.DEFAULT,
      block,
    );
    const head = result.slice(0, start);
    const content = result.slice(start, end);
    const tail = result.slice(end);

    result = `${head}${tag.startTag}${content}${tag.endTag}${tail}`;
    globalOffset += tag.startTag.length;
    globalOffset += tag.endTag.length;
  }

  return result;
};

const parseChatMessage = (
  chat_text: ChatMessageT[],
  source_type: SOURCE_TYPE,
): ChatMessageT[] => {
  if (chat_text) {
    const chatText = [] as ChatMessageT[];
    chat_text.forEach((c) => {
      // Add a URL to the default avater image for messages from Intercom
      // if the author.type is 1 (BOT) or 2 (AGENT) and its profile_url is blank
      if (source_type === SOURCE_TYPE.INTERCOM && c.author) {
        if (c.author.type === 1 && !c.author.profile_url) {
          c.author.profile_url = '/assets/avatar-intercom-operator.png';
        } else if (c.author.type === 2 && !c.author.profile_url) {
          c.author.profile_url = '/assets/avatar-intercom-admin.png';
        }
      }
      chatText.push(c);
    });
    return chatText;
  }
  return [];
};

const feedbackDetailUtils = {
  parseChatMessage,
  getTagByBlockType,
  setBlockTags,
};

export default feedbackDetailUtils;
